import { useEffect, useRef, useState } from 'react'

// ── usePromptSlot ─────────────────────────────────────────────────────────────
// A single-slot coordinator for full-screen / banner prompts (What's New, the
// plans check-in, the install banner, …). Left uncoordinated they all fire on their
// own timers at app load and STACK on top of each other, some auto-dismissing before
// the user can act (Jason 2026-06-30). This serializes them: only one shows at a
// time, higher-priority first, with a cooldown gap after each closes so the user
// gets a beat to interact before the next appears.
//
//   const granted = usePromptSlot({ id: 'whatsnew', want: showSheet, priority: 80 })
//   if (!granted) return null   // …otherwise render the prompt
//
// `want` = "I have something to show right now". The hook returns `granted` once it
// wins the slot; it holds until `want` goes false (dismissed) or the component
// unmounts, then stamps a cooldown so the next prompt waits its turn.

const waiters = new Map()   // id -> priority (prompts wanting the slot but not yet granted)
const preempters = new Set() // ids allowed to evict a lower-priority holder (opt-in)
let holderId = null         // id currently occupying the single slot
let holderPriority = 0      // its priority, so a more urgent prompt can take over
let lastClosed = 0          // timestamp the slot last freed up
const subs = new Set()
const notify = () => subs.forEach(fn => { try { fn() } catch {} })

// A waiter that outranks the CURRENT HOLDER evicts it. Without this, priority only ever
// broke ties between simultaneous waiters — whoever claimed the slot first kept it for as
// long as it wanted to show, and a more urgent prompt queued behind it forever.
//
// That is not hypothetical: Tonight's "Catch up on your plans" check-in (priority 50)
// claims the slot the moment its data loads, and holds while `want` stays true — i.e.
// until the user acts on it. The update-ready prompt (priority 80) only wants the slot
// once checkVersion runs at t=9s, so it arrived to a taken slot and could never take
// over. Users sat on a stale build with a live update they were never offered, which is
// exactly the "installed PWA stuck on an old bundle" failure the update path was
// rewritten to end (Jason, on 15.731 with 15.736 live, 2026-07-27).
// Only prompts that opt in with `preempt: true` can evict — deliberately NOT every
// higher-priority waiter. Letting raw priority preempt also let the What's-New changelog
// sheet yank away the plans check-in mid-read, which is a worse experience than waiting
// its turn. Eviction is for the one case where waiting is genuinely harmful: a downloaded
// update the user is never told about. Everything else queues as before.
function preemptIfOutranked() {
  if (!holderId) return
  let best = null
  for (const [wid, wp] of waiters) if (preempters.has(wid) && (!best || wp > best.p)) best = { id: wid, p: wp }
  if (best && best.p > holderPriority) {
    holderId = null
    holderPriority = 0
    lastClosed = 0     // an eviction is urgent; don't make the winner sit out the cooldown
  }
}

function isNextInLine(id, gap) {
  if (holderId && holderId !== id) return false          // someone else is showing
  if (Date.now() - lastClosed < gap) return false        // cooldown, give a beat
  // Highest priority among everyone currently waiting wins; ties break by id so the
  // choice is deterministic (never two grabbing the slot on the same tick).
  let best = null
  for (const [wid, wp] of waiters) {
    if (!best || wp > best.p || (wp === best.p && wid < best.id)) best = { id: wid, p: wp }
  }
  return !best || best.id === id
}

export function usePromptSlot({ id, want, priority = 50, gap = 1100, preempt = false }) {
  const [granted, setGranted] = useState(false)
  const grantedRef = useRef(false)
  const setBoth = (v) => { grantedRef.current = v; setGranted(v) }

  useEffect(() => {
    if (!id) return

    const release = () => {
      waiters.delete(id)
      preempters.delete(id)
      if (holderId === id) { holderId = null; holderPriority = 0; lastClosed = Date.now() }
      if (grantedRef.current) setBoth(false)
    }

    if (!want) { release(); notify(); return }

    // Want the slot: join the queue and try to claim it whenever things change.
    waiters.set(id, priority)
    if (preempt) preempters.add(id); else preempters.delete(id)
    preemptIfOutranked()
    const evaluate = () => {
      // Still holding? Only keep rendering while we ARE the holder — if something more
      // urgent evicted us, stand down so it can show.
      if (grantedRef.current) { if (holderId !== id) setBoth(false); return }
      if (isNextInLine(id, gap)) {
        waiters.delete(id)
        holderId = id
        holderPriority = priority
        setBoth(true)
        notify()          // others recompute (now blocked behind us)
      }
    }
    subs.add(evaluate)
    const iv = setInterval(evaluate, 350)   // fallback tick so the cooldown clears
    evaluate()
    notify()              // announce we're waiting so peers re-rank priority

    return () => {
      clearInterval(iv)
      subs.delete(evaluate)
      release()           // want changed / unmounted → free our claim
      notify()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, want, priority, gap])

  return granted
}
